<template>
    <div
        v-if="visible"
        class="fixed w-screen h-screen left-0 top-0 bg-black/60 z-10 flex items-center justify-center cursor-pointer"
        @keydown.esc="hide"
        @click.self.exact="hide"
    >
        <div v-if="internalPendingTransaction" class="bg-base-100 rounded w-96 p-2">
            <div class="legend">Betaling til</div>
            <div class="text-lg">{{ internalPendingTransaction.debitSubAccountName }}</div>
            <div class="legend mt-4">Status</div>
            <div class="text-lg">{{ internalPendingTransaction.status }} / {{ internalPendingTransaction.paymentStatus }}</div>
            <div class="legend mt-4">Mottakers kontonummer</div>
            <div class="font-mono">
                {{ formatBankAccountNo(internalPendingTransaction.debitBankAccountNumber || '') }}
            </div>
            <div class="legend mt-4">Betalingsdato</div>
            <div>
                <date-picker v-model="date"/>
            </div>
            <div class="legend mt-4">Beløp</div>
            <div>
                <currency-amount-input v-model="internalPendingTransaction.amount" class=""/>
            </div>
            <div class="legend mt-4">Fra konto</div>
            <account-picker
                v-model:account-no="internalPendingTransaction!.creditAccountNo"
                :accounts="bankAccounts"
                single-pane
                show-bank-info
                class=""
            />
            <div v-if="potentialProblem" class="text-error">{{ potentialProblem }}</div>
            <div class="flex justify-end mt-6 gap-2">
                <div class="grow">
                    <i class="i-ph-bug text-base-content/40 mr-1 inline-block w-2" @click="showDebug"/>
                </div>
                <button
                    class="btn btn-outline"
                    @click="hide"
                >
                    Avbryt
                </button>
                <button
                    class="btn btn-primary"
                    @click="saveChanges"
                >
                    Lagre
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">

import {computed, ref} from 'vue'
import {ExtendedPendingTransaction} from '@/pending-transaction/pendingTransaction.types'
import AccountPicker from '@/account-picker/AccountPicker.vue'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import {accountNoIsValidBankRange, formatBankAccountNo} from '@/bank/bank-account.service'
import DatePicker from '@/dateUtils/DatePicker.vue'
import CurrencyAmountInput from '@/components/CurrencyAmountInput.vue'
import {updatePendingTransaction} from '@/pending-transaction/pendingTransaction.service'
import {formatDate} from 'date-fns'

const emit = defineEmits(['success'])
const bankAccounts = computed(() => bookkeepingAccounts.value.filter(a => accountNoIsValidBankRange(a.accountNo)))
const potentialProblem = ref()
const date = computed({
    get: () => new Date(internalPendingTransaction.value!.date),
    set: (value: Date) => internalPendingTransaction.value!.date = value
})

const internalPendingTransaction = ref<ExtendedPendingTransaction>()

const visible = ref(false)
const hide = () => {
    visible.value = false
    internalPendingTransaction.value = undefined
}

const show = (pendingTransaction?: ExtendedPendingTransaction) => {
    visible.value = true
    if (pendingTransaction)
        internalPendingTransaction.value = {...pendingTransaction}
}

const saveChanges = async () => {
    if (!internalPendingTransaction.value)
        return
    if (!internalPendingTransaction.value.uuid)
        return
    if (!internalPendingTransaction.value.date)
        return
    if (!internalPendingTransaction.value.amount)
        return
    if (!internalPendingTransaction.value.creditAccountNo)
        return
    const updateResult = await updatePendingTransaction({
        pendingTransactionUuid: internalPendingTransaction.value.uuid,
        date: formatDate(internalPendingTransaction.value.date, 'yyyy-MM-dd'),
        amount: internalPendingTransaction.value.amount,
        creditAccountNo: internalPendingTransaction.value.creditAccountNo
    })

    //Left: Problem
    if (updateResult.isLeft()) {
        potentialProblem.value = updateResult.error
    } else {
        potentialProblem.value = undefined
        emit('success', updateResult.value)
        hide()
    }
}

const showDebug = () => {
    alert(JSON.stringify(internalPendingTransaction.value, null, 2))
}

defineExpose({
    show,
    hide
})

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.legend {
    @apply text-sm;
}

</style>